import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Edit3, Trash2, Plus, Flame, Star, Search, RefreshCw, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatPrice } from "@/components/PropertyCard";

export default function AdminProjects() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [reseeding, setReseeding] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const isAdmin = user && user !== false && user.role === "admin";

  useEffect(() => {
    if (user === false) return;
    if (user && !isAdmin) {
      navigate("/");
      return;
    }
    if (isAdmin) loadProjects();
  }, [user, isAdmin, navigate]);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const r = await api.get("/projects?limit=200");
      setProjects(r.data || []);
    } catch (e) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      setProjects(projects.filter((p) => p.id !== id));
      setConfirmDelete(null);
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const handleReseed = async () => {
    if (!window.confirm("This will DROP all existing projects and re-seed with the default catalogue. Continue?")) return;
    setReseeding(true);
    try {
      const r = await api.post("/admin/reseed-projects");
      toast.success(`Re-seeded ${r.data.projects_seeded} projects`);
      await loadProjects();
    } catch (e) {
      toast.error("Re-seed failed");
    } finally {
      setReseeding(false);
    }
  };

  if (user === false) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  if (!isAdmin) return null;

  const filtered = projects.filter(
    (p) =>
      !search ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.city.toLowerCase().includes(search.toLowerCase()) ||
      p.developer.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="admin-projects-page" className="max-w-7xl mx-auto px-4 md:px-8 py-12">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline mb-2">Admin · Project CMS</div>
          <h1 className="font-serif text-4xl font-bold text-slate-900">Manage Projects</h1>
          <p className="text-slate-600 mt-2">Edit prices, images, hot-deals and content for all {projects.length} projects.</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleReseed}
            disabled={reseeding}
            data-testid="admin-reseed-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm font-semibold disabled:opacity-50"
          >
            {reseeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Re-seed defaults
          </button>
          <Link
            to="/admin/inquiries"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm font-semibold"
          >
            View Inquiries
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mb-6 flex items-center gap-3">
        <Search className="w-5 h-5 text-slate-400 ml-2" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by project, developer or city"
          data-testid="admin-search"
          className="flex-1 bg-transparent border-0 focus:outline-none text-sm text-slate-900 py-2"
        />
        <span className="text-xs text-slate-500 px-2">{filtered.length} match{filtered.length !== 1 ? "es" : ""}</span>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-20 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-slate-500">No projects match.</div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-projects-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 font-semibold">
              <tr>
                <th className="text-left px-4 py-3">Project</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">City</th>
                <th className="text-left px-4 py-3">Price</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Status</th>
                <th className="text-center px-4 py-3">Tags</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} data-testid={`admin-row-${p.id}`} className="border-t border-slate-100 hover:bg-amber-50/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img src={p.images?.[0] || "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=200"} alt="" className="w-12 h-12 rounded-lg object-cover" />
                      <div>
                        <div className="font-semibold text-slate-900">{p.name}</div>
                        <div className="text-xs text-slate-500">{p.developer}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-slate-700">{p.city}</td>
                  <td className="px-4 py-3 font-semibold text-[#D4A437]">{formatPrice(p.price_min, p.currency)}</td>
                  <td className="px-4 py-3 hidden lg:table-cell"><span className="text-xs text-slate-600 capitalize">{p.status?.replace("_", " ")}</span></td>
                  <td className="px-4 py-3 text-center">
                    <div className="inline-flex gap-1.5 items-center">
                      {p.featured && <span className="text-[10px] bg-[#D4A437] text-white px-1.5 py-0.5 rounded font-bold tracking-wide" title="Featured"><Star className="w-3 h-3 inline" fill="currentColor" /></span>}
                      {p.hot_deal && <span className="text-[10px] bg-[#EF4444] text-white px-1.5 py-0.5 rounded font-bold inline-flex items-center gap-0.5" title={`${p.discount_pct}% off`}><Flame className="w-3 h-3" fill="currentColor" />{p.discount_pct}%</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Link
                      to={`/admin/projects/${p.id}/edit`}
                      data-testid={`admin-edit-${p.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors mr-2 font-semibold"
                    >
                      <Edit3 className="w-3.5 h-3.5" /> Edit
                    </Link>
                    <button
                      onClick={() => setConfirmDelete(p)}
                      data-testid={`admin-delete-${p.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors font-semibold"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[80] flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
              <div>
                <h3 className="font-serif text-lg font-bold text-slate-900">Delete this project?</h3>
                <p className="text-sm text-slate-600 mt-1">{confirmDelete.name} · {confirmDelete.developer} · {confirmDelete.city}. This cannot be undone.</p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg font-semibold">Cancel</button>
              <button onClick={() => handleDelete(confirmDelete.id)} className="px-4 py-2 text-sm bg-red-600 text-white hover:bg-red-700 rounded-lg font-semibold">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
